import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { CircleNotch, CheckCircle, Warning } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicApi = axios.create({ baseURL: API });

const VENDORS = [
  "FortiGate",
  "FortiManager",
  "FortiAnalyzer",
  "WiFi",
  "FortiSIEM",
  "FortiSASE and ZTNA",
  "Palo Alto Prisma",
  "Palo Alto Panorama",
  "Palo Alto Firewall",
  "Check Point",
  "Cisco Nexus",
  "Cisco Routers and Switches",
  "F5 LTM",
  "F5 GTM",
  "Sophos",
  "Zscaler",
  "Juniper",
  "Azure infrastructure",
  "AWS infrastructure",
  "GCP infrastructure",
];
const SKILL_LIB = ["Cable Management", "Rack & Stack", "Remote Hands", "Console Access", "Fibre Splicing", "Circuit Testing"];
const COMMON_CERTS = {
  "Cisco": ["CCNA", "CCNP Enterprise", "CCNP Security", "CCIE Enterprise", "DevNet Associate"],
  "Fortinet": ["NSE 1", "NSE 2", "NSE 3", "NSE 4", "NSE 5", "NSE 6", "NSE 7", "NSE 8"],
  "Palo Alto": ["PCNSA", "PCNSE", "PCCET", "PCCSE"],
  "F5": ["F5 CA", "F5 CTS", "F5 CE"],
  "Checkpoint": ["CCSA", "CCSE", "CCSM"],
  "Sophos": ["Sophos Certified Engineer", "Sophos Certified Architect", "Sophos Certified Technician (XG/XGS)", "Central Endpoint Certified Engineer"],
  "Microsoft Azure": ["AZ-900 Fundamentals", "AZ-104 Administrator", "AZ-305 Solutions Architect", "AZ-500 Security Engineer", "AZ-700 Network Engineer"],
  "AWS": ["Cloud Practitioner (CLF-C02)", "Solutions Architect Associate (SAA)", "Solutions Architect Professional (SAP)", "Advanced Networking Specialty (ANS)", "Security Specialty (SCS)"],
  "Google Cloud (GCP)": ["Cloud Digital Leader", "Associate Cloud Engineer (ACE)", "Professional Cloud Architect (PCA)", "Professional Cloud Network Engineer (PCNE)", "Professional Cloud Security Engineer"],
};

const COUNTRY_META = {
  AU: { label: "Australia", currency: "AUD", timezone: "Australia/Sydney", code: "+61", tax_ids: [
    { kind: "ABN", label: "ABN (Australian Business Number)", hint: "11 digits" },
    { kind: "ACN", label: "ACN (Australian Company Number)", hint: "9 digits" },
  ]},
  NZ: { label: "New Zealand", currency: "NZD", timezone: "Pacific/Auckland", code: "+64", tax_ids: [
    { kind: "NZBN", label: "NZBN (NZ Business Number)", hint: "13 digits" },
  ]},
  IN: { label: "India", currency: "INR", timezone: "Asia/Kolkata", code: "+91", tax_ids: [
    { kind: "PAN", label: "PAN (Permanent Account Number)", hint: "10 chars — required for any income" },
    { kind: "GSTIN", label: "GSTIN (Goods & Services Tax)", hint: "15 chars — required if you charge GST" },
    { kind: "CIN", label: "CIN (Corporate Identity Number)", hint: "21 chars — only if you invoice via a Pvt Ltd" },
  ]},
  US: { label: "United States", currency: "USD", timezone: "America/New_York", code: "+1", tax_ids: [
    { kind: "EIN", label: "EIN (Employer Identification Number)", hint: "if you invoice via a company" },
  ]},
  PH: { label: "Philippines", currency: "PHP", timezone: "Asia/Manila", code: "+63", tax_ids: [
    { kind: "TIN", label: "TIN (Tax Identification Number)", hint: "9 or 12 digits" },
    { kind: "SEC", label: "SEC Registration (if incorporated)", hint: "optional" },
  ]},
  OTHER: { label: "Other", currency: "USD", timezone: "UTC", code: "+", tax_ids: [] },
};

export default function OnboardPersonnel() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", role_title: "",
    person_type: ["engineer"],
    country: "",
    country_other: "",  // free-text country name when 'OTHER' is picked
    region: "", address: "",
    country_code: "+91", contact_phone: "",
    national_id: "",
    abn_number: "",
    abn_status: "",
    tax_ids: [],
    hourly_rate: 0,
    currency: "",
    timezone: "",
    rate_negotiable: false,
    indemnity_insurance: "",
    background_check_ok: false,
    has_own_equipment: false,
    contract_experience: "",
    vendors: [], skills: [], notes: "",
    certifications: [],
    availability_weeks: 2,
    availability_start_date: new Date().toISOString().slice(0, 10),
    availability: [],
    accept_no_misleading: false,
  });
  const [applicantId, setApplicantId] = useState("");
  // Personal details are collected via a separate Microsoft Forms link (data
  // sovereignty — sensitive PII lives in the SoverenX M365 tenant, not this
  // application). Candidate must confirm they've submitted it before we
  // accept their SoverenX prescreening.
  const [msFormAcknowledged, setMsFormAcknowledged] = useState(false);
  const MS_FORM_URL_TEMPLATE = process.env.REACT_APP_MS_DETAILS_FORM_URL || "";
  // MS Forms hides its URL pre-fill parameter names in most tenants — so instead
  // of auto-injecting the applicant ID we show a Copy button and instruct the
  // candidate to paste it into the first question of the MS Form. Simpler,
  // more robust, and works regardless of any MS Forms UI updates.
  const msFormUrl = MS_FORM_URL_TEMPLATE;

  // (Re-)generate availability rows whenever the range or start date changes
  useEffect(() => {
    const days = (form.availability_weeks || 1) * 7;
    const start = new Date(form.availability_start_date || new Date().toISOString().slice(0, 10));
    const existing = Object.fromEntries((form.availability || []).map((a) => [a.date, a]));
    const rows = [];
    for (let i = 0; i < days; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const iso = d.toISOString().slice(0, 10);
      rows.push(existing[iso] || { date: iso, available_from: "", available_to: "", unavailable_from: "", unavailable_to: "", notes: "" });
    }
    setForm((f) => ({ ...f, availability: rows }));
    // eslint-disable-next-line
  }, [form.availability_weeks, form.availability_start_date]);

  const [copySource, setCopySource] = useState(null); // { available_from, available_to, unavailable_from, unavailable_to, notes }
  const copyFrom = (row) => {
    const { available_from, available_to, unavailable_from, unavailable_to, notes } = row;
    setCopySource({ available_from, available_to, unavailable_from, unavailable_to, notes });
    toast.success("Copied — click 'Paste' on other days to reuse this");
  };
  const pasteTo = (idx) => {
    if (!copySource) { toast.error("Nothing copied yet"); return; }
    setForm((f) => ({ ...f, availability: f.availability.map((r, i) => i === idx ? { ...r, ...copySource } : r) }));
  };
  const pasteToAllWeekdays = () => {
    if (!copySource) { toast.error("Nothing copied yet"); return; }
    setForm((f) => ({
      ...f,
      availability: f.availability.map((r) => {
        const d = new Date(r.date);
        const dow = d.getDay(); // 0=Sun, 6=Sat
        return (dow >= 1 && dow <= 5) ? { ...r, ...copySource } : r;
      })
    }));
    toast.success("Applied to Mon-Fri");
  };
  const clearRow = (idx) => setForm((f) => ({ ...f, availability: f.availability.map((r, i) => i === idx ? { date: r.date, available_from: "", available_to: "", unavailable_from: "", unavailable_to: "", notes: "" } : r) }));

  useEffect(() => {
    publicApi.get(`/onboard/personnel/${token}`)
      .then((r) => {
        setMeta(r.data);
        if (r.data.applicant_id) setApplicantId(r.data.applicant_id);
        // If admin requested edits, the /onboard endpoint may return the previous submission — pre-fill.
        if (r.data.previous_submission) {
          setForm((f) => ({ ...f, ...r.data.previous_submission }));
        } else {
          setForm((f) => ({
            ...f,
            email: r.data.invited_email || "",
            person_type: [r.data.role_hint || "engineer"],
          }));
        }
      })
      .catch(() => setMeta({ status: "invalid" }))
      .finally(() => setLoading(false));
  }, [token]);

  const toggleArr = (key, v) =>
    setForm((f) => {
      const nextList = f[key].includes(v) ? f[key].filter((x) => x !== v) : [...f[key], v];
      const next = { ...f, [key]: nextList };
      // If a vendor was de-selected, drop any certifications tied to that vendor.
      if (key === "vendors" && f.certifications?.length) {
        next.certifications = f.certifications.filter((c) => nextList.includes(c.vendor));
      }
      return next;
    });

  const submit = async () => {
    if (!form.country) { toast.error("Please select your country of residence"); return; }
    if (form.country === "OTHER" && !(form.country_other || "").trim()) { toast.error("Please type your country of residence in the free-text box"); return; }
    if (!form.name.trim()) { toast.error("Full name is required"); return; }
    if (!form.accept_no_misleading) { toast.error("Please confirm your acknowledgement about accurate information"); return; }
    if (!msFormAcknowledged) { toast.error("Please confirm you have submitted the Personal Details form (Microsoft) before submitting your prescreening"); return; }
    setSaving(true);
    try {
      const r = await publicApi.post(`/onboard/personnel/${token}/submit`, form);
      setApplicantId(r.data?.applicant_id || "");
      setSubmitted(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7FAFC] flex items-center justify-center">
        <div className="text-xs font-mono uppercase tracking-wider text-slate-500">Loading…</div>
      </div>
    );
  }
  if (!meta || meta.status === "invalid" || meta.status === "revoked") {
    return <Screen icon="warn" title="Link not valid" body="This prescreening link is invalid, revoked, or has already been used. Please contact the person who invited you for a fresh link." />;
  }
  if (meta.status === "expired") {
    return <Screen icon="warn" title="Link expired" body="This prescreening link has expired. Please request a new one from the administrator." />;
  }
  if (meta.status === "submitted" || meta.status === "held" || submitted) {
    return <Screen icon="ok" title="Prescreening submitted for review"
      body="Thanks — your prescreening details have been sent to the SoverenX team. You will hear back once your application is reviewed."
      applicantId={applicantId} />;
  }
  if (meta.status === "approved") {
    return <Screen icon="ok" title="Application approved" body="Your prescreening has been approved. Check your email for the next step — Document Screening." />;
  }
  if (meta.status === "rejected") {
    return <Screen icon="warn" title="Submission closed" body="This invitation is no longer active. Please contact the administrator." />;
  }

  const isEditMode = meta.status === "edit_requested";

  return (
    <div className="min-h-screen bg-[#F7FAFC] text-[#0F1E38]" data-testid="onboard-page">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <header className="mb-8 pb-6 border-b border-slate-200">
          <div className="flex items-center justify-between mb-4 gap-4">
            <img
              src="/soverenx-logo.jpg"
              alt="SoverenX Systems"
              data-testid="onboard-logo"
              className="h-12 w-auto rounded bg-black px-2"
            />
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-slate-500 text-right">Candidate Prescreening</div>
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Candidate Prescreening</h1>
          <p className="mt-3 text-sm text-slate-500 max-w-2xl leading-relaxed">
            Fill in your prescreening details below. Everything is submitted directly to SoverenX Systems for review — no account or password needed. On approval, you will move to Document Screening.
          </p>
          {meta.note && (
            <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
              <span className="font-semibold text-[11px] uppercase tracking-wider text-amber-800 block mb-1">Note from admin</span>
              {meta.note}
            </div>
          )}

          {isEditMode && (meta.edit_note || meta.status === "edit_requested") && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-900">
              <span className="font-semibold text-[11px] uppercase tracking-wider text-blue-800 block mb-1">Admin has requested changes</span>
              {meta.edit_note || "Please review and update the information you originally submitted, then submit again."}
            </div>
          )}

          {/* Personal Details — Microsoft Forms (Step 1, mandatory before Submit).
              Personal PII (address, ID, tax docs) is collected in a separate
              Microsoft Form so that sensitive data stays inside SoverenX's own
              M365 tenant and never lands in the SoverenX application database.
              The candidate copies their Application ID here and pastes it into
              the first question of the MS Form so responses can be tied back
              to the SoverenX record. */}
          {msFormUrl && (
            <div data-testid="ms-form-banner" className="mt-5 p-5 rounded-lg border-2 border-[#FFC107] bg-[#FFFBEB]">
              <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#B7791F] mb-2">Step 1 of 2 &middot; Required before submit</div>
              <div className="font-display text-xl font-bold text-[#0F1E38] mb-2">Submit your Personal Details (Microsoft Forms)</div>
              <div className="text-sm text-slate-700 leading-relaxed mb-4">
                For your privacy, we collect your personal details through a secure Microsoft Forms link — hosted in SoverenX's own Microsoft 365 tenant. This information is <strong>not stored in this prescreening application</strong>.
              </div>

              {/* Instructions */}
              <div className="p-4 bg-white border border-amber-200 rounded mb-4">
                <div className="text-[11px] font-mono uppercase tracking-wider text-[#B7791F] font-bold mb-2">How to complete this step</div>
                <ol className="text-[13px] text-slate-700 leading-relaxed space-y-1.5 list-decimal ml-5">
                  <li><strong>Copy your Application ID</strong> below (click the yellow <em>Copy</em> button).</li>
                  <li>Click <strong>&laquo;Open Personal Details Form&raquo;</strong> — it opens in a new tab.</li>
                  <li>Paste the Application ID into the <strong>first question</strong> of the Microsoft Form.</li>
                  <li>Fill in the rest of the form and submit it.</li>
                  <li>Come back to this tab and tick the confirmation box below.</li>
                </ol>
              </div>

              {/* Application ID with Copy button */}
              <div className="p-4 bg-white border-2 border-amber-300 rounded mb-4 flex items-center justify-between gap-4">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1">Your Application ID</div>
                  <div data-testid="ms-form-applicant-id" className="text-2xl font-mono font-bold text-[#0F1E38] tracking-wider">{applicantId || "—"}</div>
                </div>
                <button
                  type="button"
                  data-testid="ms-form-copy-id"
                  onClick={async () => {
                    if (!applicantId) return;
                    try {
                      await navigator.clipboard.writeText(applicantId);
                      toast.success(`Application ID ${applicantId} copied — paste it into the MS Form`);
                    } catch {
                      toast.error("Copy failed — please select the ID and copy it manually");
                    }
                  }}
                  className="px-4 py-2.5 rounded bg-[#FFC107] hover:bg-[#E5A906] text-[#0F1E38] font-mono uppercase text-xs tracking-wider font-bold transition whitespace-nowrap"
                >
                  Copy ID
                </button>
              </div>

              <a
                data-testid="ms-form-open-btn"
                href={msFormUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-[#0F1E38] hover:bg-[#1E3A5F] text-white font-mono uppercase text-xs tracking-wider font-bold transition"
              >
                Open Personal Details Form &rarr;
              </a>
              <label className="mt-4 flex items-start gap-3 p-3 rounded border border-amber-300 bg-white cursor-pointer hover:bg-amber-50 transition">
                <input
                  data-testid="ms-form-confirm-checkbox"
                  type="checkbox"
                  checked={msFormAcknowledged}
                  onChange={(e) => setMsFormAcknowledged(e.target.checked)}
                  className="mt-1 h-4 w-4 accent-[#FFC107]"
                />
                <span className="text-sm text-slate-800 leading-relaxed">
                  <strong>I confirm I have opened and submitted the Personal Details form</strong> with my Application ID <span className="font-mono font-bold text-[#0F1E38]">{applicantId || "—"}</span> entered as the first answer. I understand my prescreening cannot be reviewed without this.
                </span>
              </label>
            </div>
          )}

          {/* Warning about misleading information */}
          <div className="mt-5 p-4 bg-red-50 border-2 border-red-200 rounded flex items-start gap-3">
            <Warning size={22} className="text-red-600 flex-shrink-0 mt-0.5" weight="fill" />
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-red-800 mb-1">Important — Accuracy Requirement</div>
              <div className="text-sm text-red-900 leading-relaxed">
                Please ensure every detail you provide is <strong>truthful, accurate and current</strong>. If you sign the Contractor Agreement based on misleading, false or exaggerated information (rates, certifications, ABN status, insurance, experience, availability), it may constitute misrepresentation and lead to <strong>immediate termination of your engagement and legal dispute</strong>. If you're unsure about anything, leave it blank or ask the administrator to clarify.
              </div>
            </div>
          </div>

        </header>

        <div className="space-y-6 bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <Section title="Country of Residence">
            <div className="text-xs text-slate-500 mb-3 leading-relaxed">
              Please select your country of residence.
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {Object.entries(COUNTRY_META).map(([code, m]) => (
                <button key={code} type="button" data-testid={`country-${code}`}
                  onClick={() => setForm({
                    ...form, country: code, currency: m.currency, timezone: m.timezone,
                    country_code: m.code, tax_ids: [], abn_number: "",
                    country_other: code === "OTHER" ? form.country_other : "",
                  })}
                  className={`p-3 rounded-lg border text-left ${form.country === code ? "border-[#FFC107] bg-amber-50 ring-2 ring-amber-200" : "border-slate-300 bg-white hover:border-slate-400"}`} style={{ transition: "border-color 0.15s ease" }}>
                  <div className="font-semibold text-[#0F1E38]">{m.label}</div>
                </button>
              ))}
            </div>
            {form.country === "OTHER" && (
              <div className="mt-3">
                <label className="block text-[11px] font-mono uppercase tracking-wider text-slate-600 mb-1">Please specify your country *</label>
                <Input
                  data-testid="onb-country-other"
                  value={form.country_other || ""}
                  onChange={(e) => setForm({ ...form, country_other: e.target.value })}
                  placeholder="e.g. United Kingdom, UAE, Singapore, Canada…"
                />
              </div>
            )}
          </Section>

          {form.country && <>
          <Section title="Identity">
            <Grid>
              <Field label="Full Name *"><Input data-testid="onb-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
              <Field label="Region / Base City"><Input data-testid="onb-region" value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} placeholder="e.g. Sydney, Melbourne, Auckland" /></Field>
            </Grid>
          </Section>

          <Section title="Rate">
            <div className="p-4 bg-amber-50/50 border border-amber-200 rounded space-y-4">
              <Grid>
                <Field label={`Your Hourly Rate (${form.currency || "—"}) *`}>
                  <Input data-testid="onb-hourly-rate" type="number" min="0" value={form.hourly_rate}
                    onChange={(e) => setForm({ ...form, hourly_rate: parseFloat(e.target.value) || 0 })} className="font-mono" />
                  <div className="text-[11px] text-slate-500 mt-1">Rate in {form.currency}, inclusive of GST/VAT if you charge it.</div>
                </Field>
                <Field label="Rate Flexibility">
                  <div className="flex flex-col gap-2 mt-1">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="negotiable" checked={form.rate_negotiable === true}
                        onChange={() => setForm({ ...form, rate_negotiable: true })} className="accent-[#FFC107]" />
                      <span className="text-sm">Open to negotiate</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="negotiable" checked={form.rate_negotiable === false}
                        onChange={() => setForm({ ...form, rate_negotiable: false })} className="accent-[#FFC107]" />
                      <span className="text-sm">Won't negotiate — fixed rate</span>
                    </label>
                  </div>
                </Field>
              </Grid>
            </div>
          </Section>

          <Section title="Technology Expertise">
            <div className="text-xs text-slate-500 mb-3 leading-relaxed">
              Please select the technologies you work with. <strong>Only tick a technology if you are at Advanced or Expert level</strong> — do not select technologies where you have basic or intermediate exposure only.
            </div>
            <div className="flex flex-wrap gap-2 mt-1">
              {VENDORS.map((v) => (
                <button key={v} type="button" data-testid={`tech-${v.replace(/\s+/g, "-").toLowerCase()}`} onClick={() => toggleArr("vendors", v)}
                  className={`px-3 py-1.5 rounded text-xs font-mono uppercase border ${form.vendors.includes(v) ? "bg-[#FFC107]/20 border-[#FFC107] text-[#0F1E38]" : "border-slate-300 text-slate-500"}`}>{v}</button>
              ))}
            </div>
          </Section>

          <Section title="Field Tech Skills (optional)">
            <div className="flex flex-wrap gap-2 mt-1">
              {SKILL_LIB.map((s) => (
                <button key={s} type="button" onClick={() => toggleArr("skills", s)}
                  className={`px-2.5 py-1 rounded text-[11px] border ${form.skills.includes(s) ? "bg-violet-100 border-violet-300 text-violet-700" : "border-slate-300 text-slate-500"}`}>{s}</button>
              ))}
            </div>
          </Section>

          <Section title="Availability">
            <div className="text-xs text-slate-500 mb-3 leading-relaxed">
              Select the upcoming weeks or months that reflect your <strong>general availability</strong> — without committing beyond what you can reasonably plan for. Enter the hours you expect to be available each day, and update your schedule as your availability changes.
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              <div>
                <Label className="text-xs text-slate-600">How many weeks ahead?</Label>
                <select data-testid="avail-weeks" value={form.availability_weeks}
                  onChange={(e) => setForm({ ...form, availability_weeks: parseInt(e.target.value) })}
                  className="mt-1 w-full h-10 px-3 rounded border border-slate-300 bg-white text-sm font-mono">
                  <option value={1}>1 week (7 days)</option>
                  <option value={2}>2 weeks (14 days)</option>
                  <option value={3}>3 weeks (21 days)</option>
                  <option value={4}>4 weeks (28 days)</option>
                </select>
              </div>
              <div>
                <Label className="text-xs text-slate-600">Starting from</Label>
                <Input data-testid="avail-start" type="date" value={form.availability_start_date}
                  onChange={(e) => setForm({ ...form, availability_start_date: e.target.value })}
                  className="mt-1 font-mono" />
              </div>
            </div>

            <div className="text-[11px] text-slate-500 mb-2">Times shown in your local timezone: <span className="font-mono text-[#0F1E38]">{form.timezone || "—"}</span></div>

            {copySource && (
              <div className="mb-3 p-2 bg-emerald-50 border border-emerald-200 rounded flex items-center justify-between gap-2 text-xs">
                <span className="font-mono text-emerald-800">Copied: {copySource.available_from || "—"} to {copySource.available_to || "—"}. Tick the dates below then click:</span>
                <div className="flex gap-2">
                  <Button data-testid="paste-selected" type="button" size="sm" onClick={() => {
                    setForm((f) => ({ ...f, availability: f.availability.map((r) => r._selected ? { ...r, available_from: copySource.available_from, available_to: copySource.available_to, notes: copySource.notes || r.notes, _selected: false } : r) }));
                    toast.success("Applied to selected dates");
                  }} className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white">Paste to Selected</Button>
                  <Button type="button" size="sm" onClick={() => setCopySource(null)} variant="outline" className="h-7 text-[11px] border-slate-300">Clear</Button>
                </div>
              </div>
            )}

            <div className="border border-slate-200 rounded overflow-hidden max-h-[520px] overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 sticky top-0 z-10">
                  <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-200">
                    <th className="px-2 py-2 w-10 text-center">Pick</th>
                    <th className="px-2 py-2 w-32">Date</th>
                    <th>Available From</th>
                    <th>Available To</th>
                    <th>Notes</th>
                    <th className="w-28 text-right pr-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {form.availability.map((row, idx) => {
                    const dt = new Date(row.date);
                    const dow = dt.toLocaleDateString(undefined, { weekday: "short" });
                    const isWeekend = dt.getDay() === 0 || dt.getDay() === 6;
                    return (
                      <tr key={row.date} data-testid={`avail-row-${row.date}`} className={`border-b border-slate-100 last:border-b-0 ${isWeekend ? "bg-slate-50/40" : ""}`}>
                        <td className="text-center">
                          <input type="checkbox" checked={!!row._selected}
                            onChange={(e) => setForm({ ...form, availability: form.availability.map((r, i) => i === idx ? { ...r, _selected: e.target.checked } : r) })}
                            className="w-4 h-4 accent-[#FFC107]" />
                        </td>
                        <td className="px-2 py-2 font-mono text-[#0F1E38]">
                          <div className="font-semibold">{row.date}</div>
                          <div className="text-[10px] text-slate-500 uppercase">{dow}</div>
                        </td>
                        <td><input type="time" value={row.available_from}
                          onChange={(e) => setForm({ ...form, availability: form.availability.map((r, i) => i === idx ? { ...r, available_from: e.target.value } : r) })}
                          className="h-7 px-1 rounded border border-slate-300 text-xs font-mono w-24" /></td>
                        <td><input type="time" value={row.available_to}
                          onChange={(e) => setForm({ ...form, availability: form.availability.map((r, i) => i === idx ? { ...r, available_to: e.target.value } : r) })}
                          className="h-7 px-1 rounded border border-slate-300 text-xs font-mono w-24" /></td>
                        <td><input type="text" value={row.notes} placeholder="…"
                          onChange={(e) => setForm({ ...form, availability: form.availability.map((r, i) => i === idx ? { ...r, notes: e.target.value } : r) })}
                          className="h-7 px-1 rounded border border-slate-300 text-xs w-32" /></td>
                        <td className="text-right pr-2">
                          <button type="button" onClick={() => copyFrom(row)} title="Copy this day"
                            className="text-slate-500 hover:text-[#0F1E38] px-1" style={{ transition: "color 0.15s ease" }}>Copy</button>
                          <button type="button" onClick={() => clearRow(idx)} title="Clear this day"
                            className="text-slate-400 hover:text-red-500 px-1" style={{ transition: "color 0.15s ease" }}>✕</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="text-[11px] text-slate-500 mt-2">Leave a date blank if you have no availability that day.</div>
          </Section>

          <div className="pt-4 border-t border-slate-200 space-y-3">
            <label className="flex items-start gap-3 cursor-pointer p-3 bg-red-50 border border-red-200 rounded">
              <input data-testid="onb-no-misleading" type="checkbox" checked={form.accept_no_misleading}
                onChange={(e) => setForm({ ...form, accept_no_misleading: e.target.checked })}
                className="mt-1 w-4 h-4 accent-red-600" />
              <div>
                <div className="text-sm font-medium text-red-900">I confirm all information above is truthful and accurate</div>
                <div className="text-xs text-red-700 mt-0.5">I understand that any misleading or false information — once discovered — may lead to termination and legal dispute per the Contractor Agreement.</div>
              </div>
            </label>
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="text-xs text-slate-500 max-w-md">By submitting, you agree that SoverenX Systems may review these details for potential casual/contract engagements as described above.</div>
              <Button data-testid="onb-submit" disabled={saving} onClick={submit} className="bg-[#FFC107] hover:bg-[#E5A906] text-[#0F1E38] font-semibold h-11 px-6">
                {saving ? <><CircleNotch size={16} className="animate-spin mr-2" /> Submitting…</> : "Submit Prescreening"}
              </Button>
            </div>
          </div>
          </>}
          {!form.country && (
            <div className="text-center py-8 text-sm text-slate-500">Select a country above to see the rest of the form.</div>
          )}
        </div>

        <div className="mt-6 text-center text-[10px] text-slate-400 font-mono uppercase tracking-wider">
          Secure prescreening · SoverenX Systems
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <div className="text-[11px] font-mono uppercase tracking-[0.24em] text-slate-500 mb-3">{title}</div>
      {children}
    </section>
  );
}
function Grid({ children }) { return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>; }
function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <Label className="text-xs text-slate-600">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
function Screen({ icon, title, body, applicantId }) {
  const Icon = icon === "ok" ? CheckCircle : Warning;
  const color = icon === "ok" ? "text-emerald-600" : "text-amber-600";
  const bg = icon === "ok" ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200";
  return (
    <div className="min-h-screen bg-[#F7FAFC] flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-lg p-8 shadow-sm text-center">
        <div className={`w-12 h-12 mx-auto rounded-full flex items-center justify-center ${bg} border`}>
          <Icon size={22} className={color} weight="fill" />
        </div>
        <h2 className="mt-4 text-xl font-semibold text-[#0F1E38]">{title}</h2>
        <p className="mt-2 text-sm text-slate-500 leading-relaxed">{body}</p>
        {applicantId && (
          <div className="mt-5 py-4 px-4 bg-slate-50 border border-slate-200 rounded">
            <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-slate-500">Your application ID</div>
            <div data-testid="applicant-id" className="mt-1 text-xl font-mono font-bold text-[#0F1E38] tracking-wider">{applicantId}</div>
            <div className="mt-2 text-[11px] text-slate-500 leading-relaxed">Please save this — the SoverenX team will reference it in all communication with you. A copy has also been emailed to you.</div>
          </div>
        )}
        <div className="mt-6 pt-4 border-t border-slate-100 text-[10px] text-slate-400 font-mono uppercase tracking-wider">SoverenX Systems</div>
      </div>
    </div>
  );
}
